import {
  getBaseContractTypeForStrategy,
  getContractTypeLabelsForDisplaySymbol,
} from './strategyContractTypeOptions';
import { parseDynamicScenarioId, findEquityStrategyForRegistry } from './vectorBasketDisplay';

/**
 * Resolve instrument metadata for a strategy name.
 * DB map wins; otherwise legacy futures-family map (defaults to NQ futures).
 */
export function resolveStrategyInstrument(strategyName, instrumentMap = {}) {
  const key = String(strategyName || '').trim().toLowerCase();
  const fromDb = key ? instrumentMap[key] : null;
  if (fromDb?.displaySymbol) {
    return {
      instrumentClass: fromDb.instrumentClass || 'futures',
      displaySymbol: fromDb.displaySymbol,
    };
  }
  const legacyBase = getBaseContractTypeForStrategy(strategyName);
  return {
    instrumentClass: 'futures',
    displaySymbol: legacyBase,
  };
}

export function usesFuturesContractToggle(instrument) {
  return (instrument?.instrumentClass || 'futures') === 'futures';
}

export function contractTypeLabelsForInstrument(instrument, strategyName) {
  if (!usesFuturesContractToggle(instrument)) return null;
  const symbol = instrument?.displaySymbol
    || getBaseContractTypeForStrategy(strategyName);
  return getContractTypeLabelsForDisplaySymbol(symbol);
}

function instrumentMapSymbol(strategyName, instrumentMap) {
  const key = String(strategyName || '').trim().toLowerCase();
  if (!key) return null;
  return instrumentMap[key]?.displaySymbol || null;
}

/** Display symbol from Strategy Data / Sheet Manager when configured. */
export function resolveConfiguredDisplaySymbol(strategyName, instrumentMap, fallback = '') {
  return instrumentMapSymbol(strategyName, instrumentMap) || fallback;
}

/** Apply admin-configured instrument symbols to per-trade journal rows. */
export function applyInstrumentSymbolsToTrades(trades, { instrumentMap, strategyName } = {}) {
  if (!Array.isArray(trades) || !trades.length) return trades;
  return trades.map((trade) => {
    const name = trade?.strategyName || strategyName;
    const configured = instrumentMapSymbol(name, instrumentMap);
    if (!configured) return trade;
    if (trade.symbol === configured) return trade;
    return { ...trade, symbol: configured };
  });
}

/** Strategy Data / Sheet Manager instrument rows are keyed by equity strategy name. */
export function resolveInstrumentStrategyName({
  id,
  effectiveId,
  registryRow,
  equityAlgoName,
  equityStrategies,
}) {
  if (registryRow) {
    const match = findEquityStrategyForRegistry(registryRow, equityStrategies);
    if (match?.name) return match.name;
    const fromConfig = registryRow.dataSourceConfig?.strategyName || registryRow.displayName;
    if (fromConfig) return fromConfig;
  }

  if (effectiveId === 'vc_core_standard' || effectiveId === 'vc_core_aggressive') {
    const match = findEquityStrategyForRegistry(
      { slug: effectiveId, dataSourceConfig: {} },
      equityStrategies,
    );
    if (match?.name) return match.name;
  }

  const dynamic = parseDynamicScenarioId(id);
  if (dynamic?.kind === 'futures') return dynamic.name;
  if (dynamic?.kind === 'equity_algo') return equityAlgoName || dynamic.key;

  const raw = String(id || '').trim();
  if (raw && raw !== 'custom' && !raw.startsWith('scenario:')) return raw;
  return null;
}

/**
 * Symbol shown in List of Trades / calendar suite for a simulator scenario.
 */
export function resolveCalendarScenarioSymbol({
  id,
  effectiveId,
  registryRow,
  instrumentMap,
  presetTradeMeta,
  equityAlgoName,
  equityStrategies,
}) {
  const strategyName = resolveInstrumentStrategyName({
    id,
    effectiveId,
    registryRow,
    equityAlgoName,
    equityStrategies,
  });
  const fromInstrument = instrumentMapSymbol(strategyName, instrumentMap);
  if (fromInstrument) return fromInstrument;

  if (presetTradeMeta && Object.prototype.hasOwnProperty.call(presetTradeMeta, effectiveId)) {
    return presetTradeMeta[effectiveId].symbol;
  }

  if (strategyName) {
    return resolveStrategyInstrument(strategyName, instrumentMap).displaySymbol;
  }
  return presetTradeMeta?.[effectiveId]?.symbol || 'NQ';
}
